import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, X, Save, Edit2, Trash2, Package, Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CATEGORIES = [
  { id: 'drivers', name: 'Drivers' },
  { id: 'fairways', name: 'Fairway Woods' },
  { id: 'hybrids', name: 'Hybrids' },
  { id: 'irons', name: 'Irons' },
  { id: 'wedges', name: 'Wedges' },
  { id: 'putters', name: 'Putters' },
];

export default function OffShelfConfigManager() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    shaft_name: '',
    shaft_flex: '',
    shaft_weight: '',
    price: '',
    description: '',
    image_url: '',
    stock: 10,
    is_active: true
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await api.get('/admin/off-shelf-configs');
      setConfigs(response.data);
    } catch (error) {
      toast.error('Failed to load off-shelf configurations');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      shaft_name: '',
      shaft_flex: '',
      shaft_weight: '',
      price: '',
      description: '',
      image_url: '',
      stock: 10,
      is_active: true
    });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.category || !formData.shaft_name || !formData.shaft_flex || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/admin/off-shelf-configs', {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      });
      toast.success('Configuration created');
      setShowNewForm(false);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast.error('Failed to create configuration');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.category || !formData.shaft_name || !formData.shaft_flex || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.put(`/admin/off-shelf-configs/${editingConfig}`, {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      });
      toast.success('Configuration updated');
      setEditingConfig(null);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await api.delete(`/admin/off-shelf-configs/${configId}`);
      toast.success('Configuration deleted');
      loadConfigs();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const startEdit = (config) => {
    setEditingConfig(config.config_id);
    setFormData({
      name: config.name,
      category: config.category,
      shaft_name: config.shaft_name,
      shaft_flex: config.shaft_flex,
      shaft_weight: config.shaft_weight || '',
      price: config.price.toString(),
      description: config.description || '',
      image_url: config.image_url || '',
      stock: config.stock,
      is_active: config.is_active
    });
    setShowNewForm(false);
  };

  const cancelEdit = () => {
    setEditingConfig(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading configurations...</p>
      </div>
    );
  }

  const ConfigForm = ({ isNew }) => (
    <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        {isNew ? 'Add New Off-the-Shelf Option' : 'Edit Configuration'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div>
          <Label>Display Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Ventus Blue 6S Driver"
          />
        </div>
        
        <div>
          <Label>Category *</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Price ($) *</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="349.99"
          />
        </div>
        
        <div>
          <Label>Shaft Name *</Label>
          <Input
            value={formData.shaft_name}
            onChange={(e) => setFormData({ ...formData, shaft_name: e.target.value })}
            placeholder="e.g., Fujikura Ventus Blue"
          />
        </div>
        
        <div>
          <Label>Shaft Flex *</Label>
          <Input
            value={formData.shaft_flex}
            onChange={(e) => setFormData({ ...formData, shaft_flex: e.target.value })}
            placeholder="e.g., 6S - Stiff"
          />
        </div>
        
        <div>
          <Label>Shaft Weight</Label>
          <Input
            value={formData.shaft_weight}
            onChange={(e) => setFormData({ ...formData, shaft_weight: e.target.value })}
            placeholder="e.g., 65g"
          />
        </div>
        
        <div>
          <Label>Stock</Label>
          <Input
            type="number"
            value={formData.stock}
            onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
          />
        </div>
        
        <div>
          <Label>Image URL</Label>
          <Input
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://..."
          />
        </div>
        
        <div className="flex items-end">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm font-medium text-gray-700">Active (visible to customers)</span>
          </label>
        </div>
      </div>
      
      <div className="mb-4">
        <Label>Description</Label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of this configuration..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
          rows={2}
        />
      </div>
      
      <div className="flex items-center justify-end space-x-2">
        <Button 
          variant="outline" 
          onClick={() => {
            if (isNew) {
              setShowNewForm(false);
              resetForm();
            } else {
              cancelEdit();
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={isNew ? handleCreate : handleUpdate}
          className="bg-[#334155] text-white hover:bg-[#475569]"
        >
          <Save className="h-4 w-4 mr-2" />
          {isNew ? 'Create' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );

  // Group configs by category
  const configsByCategory = configs.reduce((acc, config) => {
    if (!acc[config.category]) acc[config.category] = [];
    acc[config.category].push(config);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Off-the-Shelf Options
          </h2>
          <p className="text-gray-600 mt-1">
            Pre-configured shaft options for quick purchase (Ready to Ship)
          </p>
        </div>
        <Button 
          onClick={() => { setShowNewForm(true); setEditingConfig(null); resetForm(); }}
          className="bg-[#334155] text-white hover:bg-[#475569]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Option
        </Button>
      </div>

      {showNewForm && <ConfigForm isNew={true} />}
      {editingConfig && <ConfigForm isNew={false} />}

      {/* Configs by Category */}
      {CATEGORIES.map(category => {
        const categoryConfigs = configsByCategory[category.id] || [];
        if (categoryConfigs.length === 0) return null;

        return (
          <div key={category.id} className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <Package className="h-5 w-5 mr-2 text-[#4A6741]" />
              {category.name}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({categoryConfigs.length} option{categoryConfigs.length !== 1 ? 's' : ''})
              </span>
            </h3>
            
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Shaft</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Flex</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {categoryConfigs.map(config => (
                    <tr key={config.config_id} className={!config.is_active ? 'bg-gray-50 opacity-60' : ''}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{config.name}</div>
                        {config.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">{config.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{config.shaft_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {config.shaft_flex}
                        {config.shaft_weight && <span className="text-gray-400 ml-1">({config.shaft_weight})</span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">${config.price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{config.stock}</td>
                      <td className="px-4 py-3">
                        {config.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Check className="h-3 w-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => startEdit(config)}
                          className="p-2 text-gray-600 hover:text-[#334155] hover:bg-gray-100 rounded-lg transition-colors mr-1"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(config.config_id)}
                          className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {configs.length === 0 && !showNewForm && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No off-the-shelf options configured yet</p>
          <Button 
            onClick={() => setShowNewForm(true)}
            className="bg-[#334155] text-white hover:bg-[#475569]"
          >
            Add Your First Option
          </Button>
        </div>
      )}
    </div>
  );
}
